import { useState, useMemo, useEffect, useRef } from 'react';
import { PersonalRecipeIndexEntry } from '../services/personalRecipeIndex';
import { detectRecipeDiet } from '../components/recipes/RecipeShelfCard';
import { expandSearchQuery } from '../services/searchableIngredients';
import { logFailedSearch } from '../services/searchTelemetryService';
import { buildSearchDocs, SearchDoc } from '../services/recipeSearchIndex';
import { tokenize, foldText } from '../utils/searchNormalize';
import {
  searchRecipeEntries,
  docMatchesTerm,
  prepareTerms,
  docMatchesAnyPreparedTerm,
  selectDiscriminatingTerms,
  rankByTerms,
  RankingTerms,
} from '../services/recipeSearchScorer';
import { fetchSearchIntent, SearchIntent } from '../services/searchIntentService';
import { DISH_TYPE_ALIASES } from '../constants/dishTypes';

export interface FilterToken {
  id: string;
  kind: 'ing' | 'time' | 'diet' | 'rate' | 'sort' | 'pantry';
  /**
   * Literal fallback text. Used verbatim for `ing` tokens, which echo the
   * user's own search words and must not be translated.
   */
  label: string;
  /**
   * i18n key for tokens whose label is app chrome rather than user input.
   * When present the renderer must prefer `t(labelKey, labelParams)` over `label`.
   */
  labelKey?: string;
  /** Interpolation values for `labelKey` (e.g. the excluded ingredient word). */
  labelParams?: Record<string, string>;
  neg?: boolean;
  /**
   * Who created this filter. Drives its colour: a filter the user typed or
   * tapped should not wear the same AI styling as one the model inferred.
   */
  source: 'user' | 'ai';
  /**
   * Lifecycle. `query` tokens belong to the search that produced them and are
   * replaced by the next search; `chip` tokens were tapped deliberately and
   * persist until tapped off.
   */
  origin: 'query' | 'chip';
  /**
   * Words the user typed that this token stands for. Ranked at full weight.
   *
   * Kept alongside `test` rather than derived from it: a predicate is opaque,
   * and the model's expansion replaces the user's own token, so without this the
   * user's words would vanish from the ranking exactly when they matter most.
   */
  userTerms?: string[];
  /**
   * Words the model inferred. Ranked at `AI_TERM_DISCOUNT`, so an inference can
   * order the list but never outrank a word the user actually wrote.
   *
   * Never set on a negation — an exclusion must not pull anything up the list.
   */
  aiTerms?: string[];
  /**
   * Predicate over a pre-folded search document. Takes the doc rather than the
   * raw recipe so text predicates match accent- and typo-insensitively; the
   * recipe itself is still available as `doc.recipe` for numeric fields.
   */
  test: (doc: SearchDoc) => boolean;
}

/** A token before provenance is stamped on it — see `stamp`. */
type TokenSpec = Omit<FilterToken, 'source' | 'origin'>;

/**
 * Stamp provenance at the point of creation rather than on every literal, so a
 * new token can never silently inherit the wrong attribution.
 */
const stamp =
  (source: FilterToken['source'], origin: FilterToken['origin']) =>
  (t: TokenSpec): FilterToken => ({ ...t, source, origin });

export interface SearchContext {
  existingTokens: FilterToken[];
  pantryItems: string[];
  userDietPrefs?: string[];
}

type RecipeMetadata = Record<string, { rating?: number }>;

/** How many near-misses to offer when a query matches nothing exactly. */
const NEAR_MISS_LIMIT = 12;

/**
 * Dish-type vocabulary key -> the existing translated meal-type label. Written
 * out rather than camel-cased on the fly so a missing translation is a visible
 * gap here instead of a silently untranslated pill.
 */
const MEAL_TYPE_LABEL_KEY: Record<string, string> = {
  breakfast: 'recipes.mealTypes.breakfast',
  'main course': 'recipes.mealTypes.mainCourse',
  dessert: 'recipes.mealTypes.dessert',
  snack: 'recipes.mealTypes.snack',
  appetizer: 'recipes.mealTypes.appetizer',
  'side dish': 'recipes.mealTypes.sideDish',
  beverage: 'recipes.mealTypes.beverage',
  salad: 'recipes.mealTypes.salad',
  soup: 'recipes.mealTypes.soup',
};

/** Tapped chips are filters and outlive a search; query tokens belong to it. */
export const keepsAcrossSearches = (t: FilterToken): boolean => t.origin === 'chip';

/**
 * Real-time search used while the user is typing (before pressing Enter).
 *
 * Ranked, accent-insensitive and typo-tolerant — see `recipeSearchScorer`.
 * Prefer the `SearchDoc[]` overload: passing raw recipes re-folds the whole
 * catalog on every call, which is fine for a one-off but wasteful per keystroke.
 */
export function liveSearch(
  rawQuery: string,
  recipesOrDocs: PersonalRecipeIndexEntry[] | SearchDoc[],
): PersonalRecipeIndexEntry[] {
  if (!rawQuery.trim()) return [];
  const docs = asSearchDocs(recipesOrDocs);
  return searchRecipeEntries(rawQuery, docs);
}

function isSearchDocArray(
  value: PersonalRecipeIndexEntry[] | SearchDoc[],
): value is SearchDoc[] {
  return value.length === 0 || 'fuzzyTokens' in value[0];
}

function asSearchDocs(value: PersonalRecipeIndexEntry[] | SearchDoc[]): SearchDoc[] {
  return isSearchDocArray(value) ? value : buildSearchDocs(value);
}

/**
 * Folded, because the tokens compared against this set are folded — an
 * accented entry like "très" would never match the "tres" the tokenizer emits.
 */
const STOP_WORDS = new Set([

  'avec', 'pour', 'un', 'une', 'des', 'de', 'du', 'le', 'la', 'les', 'et', 'ou',
  'je', 'on', 'en', 'au', 'aux', 'ma', 'mon', 'mes', 'ce', 'qui', 'que', 'est',
  'pas', 'non', 'très', 'trop', 'aussi', 'encore', 'plus', 'moins', 'fait',
  'faire', 'envie', 'recette', 'quelque', 'chose', 'truc', 'idée', 'plat',
  'a', 'an', 'the', 'of', 'in', 'with', 'for', 'and', 'or',
].map(foldText));

/**
 * French elision attaches a one-letter prefix to the following word: the word
 * in "d'été" is "été". Kept for display only — the tokenizer already drops the
 * orphaned letter, but the chip should echo the user's own spelling.
 */
function stripElision(word: string): string {
  return word.replace(/^[\p{L}]['\u2019]/u, '');
}

// Swappable NLU — replace body with an LLM call when ready
export function parseQuery(
  query: string,
  _context: SearchContext,
  pantryItems: string[],
): FilterToken[] {
  const tokens: TokenSpec[] = [];
  let remaining = query.toLowerCase().trim();

  // 1. Negation: "sans X" or "pas de X"
  const negPattern = /(sans|pas\s+de?)\s+([\wÀ-ž]+(?:\s+[\wÀ-ž]+)?)/gi;
  let negMatch: RegExpExecArray | null;
  while ((negMatch = negPattern.exec(remaining)) !== null) {
    const word = negMatch[2].trim();
    const syns = expandSearchQuery(word);
    const id = `ing_${word.replace(/\s+/g, '_')}`;
    tokens.push({
      id,
      kind: 'ing',
      label: `Sans ${word}`,
      labelKey: 'recipes.shortcuts.without',
      labelParams: { word },
      neg: true,
      // Exclusion stays literal — a fuzzy negation would hide recipes for
      // words the user never typed.
      test: (doc) => !syns.some((s) => docMatchesTerm(doc, s)),
    });
  }
  remaining = remaining.replace(negPattern, '').trim();

  // 2. Time tokens
  if (/\b(rapide|vite|express|20\s*min|≤\s*20)\b/.test(remaining)) {
    tokens.push({
      id: 'time_20',
      kind: 'time',
      label: '≤ 20 min',
      labelKey: 'recipes.shortcuts.quick',
      test: ({ recipe }) => (recipe.readyInMinutes ?? 0) > 0 && recipe.readyInMinutes <= 20,
    });
    remaining = remaining.replace(/\b(rapide|vite|express|20\s*min)\b/g, '').trim();
  } else if (/\b(30\s*min|demi.heure)\b/.test(remaining)) {
    tokens.push({
      id: 'time_30',
      kind: 'time',
      label: '≤ 30 min',
      labelKey: 'recipes.shortcuts.quick30',
      test: ({ recipe }) => (recipe.readyInMinutes ?? 0) > 0 && recipe.readyInMinutes <= 30,
    });
    remaining = remaining.replace(/\b(30\s*min|demi.heure)\b/g, '').trim();
  } else if (/\b45\s*min\b/.test(remaining)) {
    tokens.push({
      id: 'time_45',
      kind: 'time',
      label: '≤ 45 min',
      labelKey: 'recipes.shortcuts.quick45',
      test: ({ recipe }) => (recipe.readyInMinutes ?? 0) > 0 && recipe.readyInMinutes <= 45,
    });
    remaining = remaining.replace(/\b45\s*min\b/g, '').trim();
  }

  // 3. Diet tokens
  if (/\bv[eé]g[eé](tar|talien|an)?\b/i.test(remaining)) {
    tokens.push({
      id: 'diet_veg',
      kind: 'diet',
      label: 'Végétarien',
      labelKey: 'recipes.shortcuts.vegetarian',
      test: ({ recipe }) => detectRecipeDiet(recipe) === 'veg',
    });
    remaining = remaining.replace(/\bv[eé]g[eé][a-z]*\b/gi, '').trim();
  }
  if (/\b(poisson|seafood|fish|mer)\b/i.test(remaining)) {
    tokens.push({
      id: 'diet_fish',
      kind: 'diet',
      label: 'Poisson',
      labelKey: 'recipes.shortcuts.fish',
      test: ({ recipe }) => detectRecipeDiet(recipe) === 'fish',
    });
    remaining = remaining.replace(/\b(poisson|seafood|fish|mer)\b/gi, '').trim();
  }

  // 4. Rating tokens
  if (/\b(bien\s*not[eé]|★|meilleur|excellent)\b/i.test(remaining)) {
    tokens.push({
      id: 'rate_45',
      kind: 'rate',
      label: '★ 4.5+',
      labelKey: 'recipes.shortcuts.topRated',
      test: ({ recipe }) => (recipe.sourceRating ?? 0) >= 4.5,
    });
    remaining = remaining.replace(/\b(bien\s*not[eé]|meilleur|excellent)\b/gi, '').trim();
  }

  // 5. Sort tokens
  if (/\b(surprend|hasard|al[eé]atoire|surprise|d[eé]couvrir)\b/i.test(remaining)) {
    tokens.push({
      id: 'sort_random',
      kind: 'sort',
      label: 'Surprise',
      labelKey: 'recipes.shortcuts.surpriseShort',
      test: () => true,
    });
    remaining = remaining.replace(/\b(surprend|hasard|al[eé]atoire|surprise|d[eé]couvrir)\b/gi, '').trim();
  }

  // 6. Pantry token
  if (/\b(frigo|placard|ce\s+que\s+j.?ai|mon\s+frigo)\b/i.test(remaining)) {
    const captured = prepareTerms(pantryItems);
    tokens.push({
      id: 'pantry_match',
      kind: 'pantry',
      label: 'Mon frigo',
      labelKey: 'recipes.shortcuts.pantry',
      test: (doc) => captured.length > 0 && docMatchesAnyPreparedTerm(doc, captured),
    });
    remaining = remaining.replace(/\b(frigo|placard|ce\s+que\s+j.?ai|mon\s+frigo)\b/gi, '').trim();
  }

  // 7. Remaining words → ingredient token
  //
  // Uses the shared tokenizer rather than stripping punctuation in place. The
  // old `.replace(/[.,!?;:'"-]/g, '')` deleted the apostrophe and welded the
  // halves together, so "plat d'été" searched for the non-word "dété" — and
  // matched a Christmas log through the typo tier.
  const words: string[] = [];
  const display: string[] = [];
  for (const rawWord of remaining.split(/\s+/)) {
    const word = stripElision(rawWord);
    const matched = tokenize(word).filter(
      (tok) => tok.length > 2 && !STOP_WORDS.has(tok)
    );
    if (matched.length === 0) continue;
    words.push(...matched);
    display.push(word);
  }

  if (words.length > 0) {
    const combined = words.join(' ');
    const syns = expandSearchQuery(combined);
    if (syns.length > 0) {
      const id = `ing_${words[0]}`;
      // Match on folded tokens, but label with what the user actually wrote:
      // a chip reading "Ete" instead of "Été" is its own small bug.
      const shown = display.join(' ');
      tokens.push({
        id,
        kind: 'ing',
        label: shown.charAt(0).toUpperCase() + shown.slice(1),
        // The folded words, not `syns`: ranking expands each word into its own
        // OR-group, whereas the filter needs the combined phrase.
        userTerms: words,
        test: (doc) => syns.some((s) => docMatchesTerm(doc, s)),
      });
    }
  }

  // Everything the local parser produces came from words the user typed.
  return tokens.map(stamp('user', 'query'));
}

/**
 * Convert LLM-derived intent into the same `FilterToken` shape the local
 * parser produces, so the chips UI, `mergeTokens` and removal all work
 * unchanged.
 *
 * `include` terms are OR'd — the model expands a concept into many concrete
 * words ("asiatique" → miso, teriyaki, ramen), and requiring all of them would
 * match nothing.
 */
export function intentToTokens(
  intent: SearchIntent,
  /**
   * Label for the expanded-concept chip. Should be the subject words the local
   * parser attributed to the query ("Poulet"), not the whole raw query —
   * "poulet rapide" would restate the separate time chip beside it.
   */
  includeLabel: string,
  docs: SearchDoc[],
  /**
   * Words the user typed that the include token replaces. Carried onto it so
   * the ranking keeps weighting them at full strength: this token supersedes
   * the local free-text one, and with it the only other record of them.
   */
  supersededUserTerms?: string[],
): FilterToken[] {
  const tokens: TokenSpec[] = [];

  if (intent.include.length > 0) {
    // "asiatique" expands to miso, teriyaki... and also riz and ail. Since
    // these are OR'd, one catalog-wide term would match nearly everything.
    const terms = selectDiscriminatingTerms(docs, intent.include);
    tokens.push({
      id: `ai_include_${terms[0]}`,
      kind: 'ing',
      label: includeLabel.trim(),
      userTerms: supersededUserTerms?.length ? supersededUserTerms : undefined,
      aiTerms: terms,
      test: (doc) => terms.some((term) => docMatchesTerm(doc, term)),
    });
  }

  for (const term of intent.exclude) {
    tokens.push({
      id: `ing_${term.replace(/\s+/g, '_')}`,
      kind: 'ing',
      label: `Sans ${term}`,
      labelKey: 'recipes.shortcuts.without',
      labelParams: { word: term },
      neg: true,
      test: (doc) => !docMatchesTerm(doc, term),
    });
  }

  if (intent.maxMinutes !== null) {
    const limit = intent.maxMinutes;
    tokens.push({
      id: `time_${limit}`,
      kind: 'time',
      label: `≤ ${limit} min`,
      test: ({ recipe }) => (recipe.readyInMinutes ?? 0) > 0 && recipe.readyInMinutes <= limit,
    });
  }

  if (intent.diet) {
    const diet = intent.diet;
    tokens.push({
      id: `diet_${diet}`,
      kind: 'diet',
      label: diet === 'veg' ? 'Végétarien' : 'Poisson',
      labelKey: diet === 'veg' ? 'recipes.shortcuts.vegetarian' : 'recipes.shortcuts.fish',
      test: ({ recipe }) => detectRecipeDiet(recipe) === diet,
    });
  }

  if (intent.dishType) {
    // Only ~40% of recipes carry dishTypes, so this matches the aliases as text
    // rather than filtering on the field — otherwise it would hide the rest.
    const aliases = DISH_TYPE_ALIASES[intent.dishType] ?? [intent.dishType];
    tokens.push({
      id: `dish_${intent.dishType}`,
      kind: 'ing',
      label: intent.dishType,
      // The raw vocabulary key is an untranslated English enum ("main course").
      // These nine already exist, translated, as meal-type filter labels.
      labelKey: MEAL_TYPE_LABEL_KEY[intent.dishType],
      aiTerms: aliases,
      test: (doc) => aliases.some((alias) => docMatchesTerm(doc, alias)),
    });
  }

  if (intent.minRating !== null) {
    const min = intent.minRating;
    tokens.push({
      id: 'rate_45',
      kind: 'rate',
      label: '★ 4.5+',
      labelKey: 'recipes.shortcuts.topRated',
      test: ({ recipe }) => (recipe.sourceRating ?? 0) >= min,
    });
  }

  // Everything here is the model's reading of the query, not the user's words.
  return tokens.map(stamp('ai', 'query'));
}

/**
 * Merge tokens: a same-id token replaces the existing one.
 *
 * Deliberately not toggle semantics. An earlier version removed a token whose
 * id already existed, on the theory that a repeat meant "turn it off" — right
 * for a chip tap, wrong for every programmatic merge. The local parser's "sans
 * porc" and the model's "porc" exclusion share an id, so merging them cancelled
 * the exclusion and quietly served pork recipes. Chip taps do their own
 * toggling in `addShortcutToken`, so nothing needs that behaviour here.
 */
export function mergeTokens(existing: FilterToken[], incoming: FilterToken[]): FilterToken[] {
  const result = [...existing];
  for (const token of incoming) {
    const idx = result.findIndex(t => t.id === token.id);
    if (idx !== -1) {
      // The model often re-derives a filter the user stated outright ("sans
      // porc" -> exclude porc). Take the incoming predicate, but never demote
      // the user's own words to an AI inference.
      result[idx] = result[idx].source === 'user' ? { ...token, source: 'user' } : token;
    } else if (['time', 'rate', 'sort', 'pantry', 'diet'].includes(token.kind)) {
      // These are single-valued: a second one replaces the first.
      const sameKind = result.findIndex(t => t.kind === token.kind);
      if (sameKind !== -1) result[sameKind] = token;
      else result.push(token);
    } else {
      result.push(token);
    }
  }
  return result;
}

/**
 * Each word becomes its own OR-group holding its synonyms, so a French query
 * still reaches an English-sourced recipe ("poulet" ranking "Chicken Soup")
 * while separate words still each have to earn their score.
 */
function userTermGroups(words: string[]): string[][] {
  return words.map(word => expandSearchQuery(word));
}

/**
 * The words behind the current filters, split by who supplied them.
 *
 * Tokens carrying no words — a time limit, a rating floor, a tapped chip —
 * contribute nothing, which is why a chips-only search falls back to rating.
 */
function rankingTermsFor(tokens: FilterToken[]): RankingTerms {
  return {
    user: userTermGroups(tokens.flatMap(t => t.userTerms ?? [])),
    // One group per token: the terms within it are the model's alternative
    // readings of a single concept, so they compete rather than accumulate.
    ai: tokens.flatMap(t => (t.aiTerms?.length ? [t.aiTerms] : [])),
  };
}

/**
 * Filter the catalog down to the recipes passing every token, then order what
 * survives by relevance.
 *
 * The ordering half is #578: survivors used to keep catalog order, so the first
 * recipe that happened to pass won regardless of the query, and two unrelated
 * searches opened on the same recipe.
 */
export function applyTokens(tokens: FilterToken[], docs: SearchDoc[]): PersonalRecipeIndexEntry[] {
  const filtered = docs.filter(doc => tokens.every(t => t.test(doc)));
  // "Surprise" is an explicit request *not* to rank by the query.
  if (tokens.some(t => t.id === 'sort_random')) {
    return filtered
      .map(doc => doc.recipe)
      .sort((a, b) => (b.sourceRating ?? 0) - (a.sourceRating ?? 0));
  }
  return rankByTerms(filtered, rankingTermsFor(tokens));
}

export function exactSearch(query: string, docs: SearchDoc[]): PersonalRecipeIndexEntry[] {
  const syns = expandSearchQuery(query.toLowerCase());
  const filtered = docs.filter(doc => syns.some(s => docMatchesTerm(doc, s)));
  // Same bug, same fix: this path filtered without ranking too. Everything here
  // came from the user's own words, so nothing is discounted.
  const words = tokenize(query).filter(tok => tok.length > 2 && !STOP_WORDS.has(tok));
  return rankByTerms(filtered, { user: userTermGroups(words), ai: [] });
}

export function useRecipeSearch(
  allRecipes: PersonalRecipeIndexEntry[],
  pantryItems: string[],
  _recipeMetadata?: RecipeMetadata,
) {
  const [tokens, setTokens] = useState<FilterToken[]>([]);
  const [isExactSearch, setIsExactSearch] = useState(false);
  const [lastRawQuery, setLastRawQuery] = useState('');
  const [isIntentLoading, setIsIntentLoading] = useState(false);
  const resultClickedRef = useRef(false);
  /** The query whose intent request is currently in flight, for staleness checks. */
  const pendingIntentQueryRef = useRef('');
  /**
   * Local free-text tokens the model's expansion should replace, with the
   * labels and words they carried — the expansion chip reuses the user's own
   * words for its label, and the ranking keeps weighting them at full strength.
   */
  const supersededIngRef = useRef<Array<{ id: string; label: string; terms: string[] }>>([]);

  /**
   * Fold and tokenize the catalog once per load rather than per keystroke.
   * Rebuilds only when the recipe list identity changes.
   */
  const docs = useMemo(() => buildSearchDocs(allRecipes), [allRecipes]);

  // Pantry names tokenized once per pantry change, not once per recipe per tap (#668).
  const preparedPantry = useMemo(() => prepareTerms(pantryItems), [pantryItems]);

  const { results, hasNoMatch } = useMemo(() => {
    if (tokens.length === 0 && !isExactSearch) return { results: [], hasNoMatch: false };
    if (isExactSearch) return { results: exactSearch(lastRawQuery, docs), hasNoMatch: false };
    const filtered = applyTokens(tokens, docs);
    if (filtered.length > 0) return { results: filtered, hasNoMatch: false };

    // Nothing matched. Showing the entire catalog reads as a broken filter, so
    // fall back to the closest scored matches for what the user actually typed.
    //
    // OR semantics here: the whole point is that no recipe satisfies every
    // word, so requiring all of them would return nothing to fall back to.
    const nearMisses = searchRecipeEntries(lastRawQuery, docs, {
      limit: NEAR_MISS_LIMIT,
      requireAllWords: false,
      // Weak matches are the entire point here — they are labelled as
      // approximate rather than presented as answers.
      minRelevance: 0,
    });
    return { results: nearMisses, hasNoMatch: true };
  }, [tokens, isExactSearch, lastRawQuery, docs]);

  // Zero-result telemetry
  const prevZeroRef = useRef(false);
  useEffect(() => {
    if (hasNoMatch && !prevZeroRef.current) {
      logFailedSearch({
        type: 'zero_results',
        rawQuery: lastRawQuery,
        parsedTokens: tokens.map(t => ({ kind: t.kind, label: t.label, neg: t.neg })),
        resultCount: 0,
      });
    }
    prevZeroRef.current = hasNoMatch;
  }, [tokens, hasNoMatch, lastRawQuery]);

  function submitQuery(rawQuery: string) {
    if (!rawQuery.trim()) return;
    resultClickedRef.current = false;
    const context: SearchContext = { existingTokens: tokens, pantryItems };
    const incoming = parseQuery(rawQuery, context, pantryItems);

    // Phase 1: the local parser answers immediately. Chips and results appear
    // with no network wait, exactly as before.
    //
    // Remember which free-text tokens this query produced: they hold the user's
    // literal words ("asiatique"), and the model's expansion supersedes them.
    // Left in place they stay AND-ed with the expansion, which matches nothing.
    supersededIngRef.current = incoming
      .filter(t => t.kind === 'ing' && !t.neg)
      .map(t => ({ id: t.id, label: t.label, terms: t.userTerms ?? [] }));

    if (incoming.length === 0) {
      setLastRawQuery(rawQuery);
      setIsExactSearch(true);
      setTokens(prev => prev.filter(keepsAcrossSearches));
    } else {
      setIsExactSearch(false);
      setLastRawQuery(rawQuery);
      // A new search replaces the previous one. Only deliberately tapped chips
      // survive: without this, searching "dessert" after "poulet rapide" ANDs
      // the two and returns nothing, which reads as "we have no desserts".
      setTokens(prev => mergeTokens(prev.filter(keepsAcrossSearches), incoming));
    }

    // Phase 2: ask the model what the query actually meant. Best-effort — if it
    // is slow, offline or unparseable, the local result above simply stands.
    void applySearchIntent(rawQuery);
  }

  /**
   * Merge LLM-derived filters into the current tokens.
   *
   * Guarded against a stale response overwriting a newer query: by the time a
   * request resolves the user may have typed something else, and applying the
   * old answer would be worse than applying nothing.
   */
  async function applySearchIntent(rawQuery: string) {
    pendingIntentQueryRef.current = rawQuery;
    setIsIntentLoading(true);
    try {
      const intent = await fetchSearchIntent(rawQuery);
      if (pendingIntentQueryRef.current !== rawQuery) return;
      if (!intent) return;

      // Reuse the subject the local parser already isolated, so the chip reads
      // "Poulet" rather than echoing the entire query back.
      const includeLabel = supersededIngRef.current[0]?.label ?? rawQuery.trim();
      // The expansion token replaces the local one, so it has to inherit its
      // words or the user's own vocabulary drops out of the ranking.
      const supersededTerms = supersededIngRef.current.flatMap(t => t.terms);
      const intentTokens = intentToTokens(intent, includeLabel, docs, supersededTerms);
      if (intentTokens.length === 0) return;

      const superseded = new Set(
        intentTokens.some(t => t.kind === 'ing' && !t.neg)
          ? supersededIngRef.current.map(t => t.id)
          : []
      );
      setIsExactSearch(false);
      setTokens(prev =>
        mergeTokens(prev.filter(t => !superseded.has(t.id)), intentTokens)
      );
    } finally {
      if (pendingIntentQueryRef.current === rawQuery) setIsIntentLoading(false);
    }
  }

  function removeToken(id: string) {
    setTokens(prev => prev.filter(t => t.id !== id));
    if (tokens.length === 1) {
      setIsExactSearch(false);
      setLastRawQuery('');
    }
  }

  function addShortcutToken(chipId: string) {
    resultClickedRef.current = false;
    let token: TokenSpec | null = null;
    const p = preparedPantry;

    switch (chipId) {
      case 'rapide':
        token = {
          id: 'time_20',
          kind: 'time',
          label: '≤ 20 min',
      labelKey: 'recipes.shortcuts.quick',
          test: ({ recipe }) => (recipe.readyInMinutes ?? 0) > 0 && recipe.readyInMinutes <= 20,
        };
        break;
      case 'frigo':
        token = {
          id: 'pantry_match',
          kind: 'pantry',
          label: 'Mon frigo',
      labelKey: 'recipes.shortcuts.pantry',
          test: (doc) => p.length > 0 && docMatchesAnyPreparedTerm(doc, p),
        };
        break;
      case 'note':
        token = {
          id: 'rate_45',
          kind: 'rate',
          label: '★ 4.5+',
      labelKey: 'recipes.shortcuts.topRated',
          test: ({ recipe }) => (recipe.sourceRating ?? 0) >= 4.5,
        };
        break;
      case 'surprise':
        token = {
          id: 'sort_random',
          kind: 'sort',
          label: 'Surprise',
      labelKey: 'recipes.shortcuts.surpriseShort',
          test: () => true,
        };
        break;
      case 'veg':
        token = {
          id: 'diet_veg',
          kind: 'diet',
          label: 'Végétarien',
      labelKey: 'recipes.shortcuts.vegetarian',
          test: ({ recipe }) => detectRecipeDiet(recipe) === 'veg',
        };
        break;
    }

    if (!token) return;
    const t = stamp('user', 'chip')(token);
    setTokens(prev => {
      const exists = prev.some(existing => existing.id === t.id);
      if (exists) return prev.filter(existing => existing.id !== t.id);
      return mergeTokens(prev, [t]);
    });
  }

  function setExactSearch(raw: string) {
    logFailedSearch({
      type: 'exact_fallback',
      rawQuery: raw,
      parsedTokens: tokens.map(t => ({ kind: t.kind, label: t.label, neg: t.neg })),
      resultCount: results.length,
    });
    setLastRawQuery(raw);
    setIsExactSearch(true);
  }

  function reset() {
    setTokens([]);
    setIsExactSearch(false);
    setLastRawQuery('');
    resultClickedRef.current = false;
    // Invalidate any in-flight intent request so a late response cannot
    // repopulate a search the user just cleared.
    pendingIntentQueryRef.current = '';
    setIsIntentLoading(false);
  }

  function signalAbandoned() {
    if (tokens.length > 0 && !resultClickedRef.current) {
      logFailedSearch({
        type: 'abandoned',
        rawQuery: lastRawQuery,
        parsedTokens: tokens.map(t => ({ kind: t.kind, label: t.label, neg: t.neg })),
        resultCount: results.length,
      });
    }
  }

  function signalResultClicked() {
    resultClickedRef.current = true;
  }

  return {
    tokens,
    results,
    hasNoMatch,
    /**
     * The folded catalog. Exposed so the live-search-while-typing path reuses
     * this array instead of rebuilding it — and so both paths share the
     * scorer's vocabulary cache, which is keyed on array identity.
     */
    searchDocs: docs,
    inlineAnswer: null as string | null,
    isFollowUpLoading: isIntentLoading,
    submitQuery,
    removeToken,
    addShortcutToken,
    setExactSearch,
    reset,
    signalAbandoned,
    signalResultClicked,
    isExactSearch,
    lastRawQuery,
  };
}
